import { createContext, use } from 'react';

import type { Session } from './Session.ts';

/**
 * React context carrying a conversation-scoped `Session`. The provider
 * is mounted at the conversation root by the chat plugin (or any other
 * consumer); engine consumers read it via `useSession()` /
 * `useEvalExpr()`.
 *
 * The context's null default is intentional — `useSession()` throws if
 * no provider is mounted upstream, so production code never silently
 * falls back to a fresh per-call in-memory session that would mask a
 * missing provider as "works, but bindings disappear between turns".
 *
 * Mount with React 19's context-as-provider syntax:
 *
 * ```tsx
 * <SessionContext value={session}>
 *   <ChatThread />
 * </SessionContext>
 * ```
 *
 * @see §7.10 — Session interface (conversation-scoped, lives outside Env)
 * @see §7.11 — React integration design
 */
export const SessionContext = createContext<Session | null>(null);
SessionContext.displayName = 'ExprSessionContext';

/**
 * Read the current `Session` from React context.
 *
 * @throws if no `SessionContext` provider is mounted upstream. The
 *   error message includes the mounting recipe so callers can fix it
 *   without leaving the failing component.
 */
export function useSession(): Session {
  const session = use(SessionContext);
  if (session === null) {
    throw new Error(
      'useSession() requires a <SessionContext> provider. ' +
        'Mount `<SessionContext value={session}>...</SessionContext>` at the conversation root.',
    );
  }
  return session;
}
